import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Search, Plus, Trash2, ShieldCheck, ShieldOff, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RULE_TYPE_META = {
  locked_rate: { label: 'Locked Bill Rate', kind: 'decimal', suffix: '$/hr' },
  locked_pay_rate: { label: 'Locked Pay Rate (talent)', kind: 'decimal', suffix: '$/hr' },
  role_block: { label: 'Blocked Role', kind: 'role_pair' },
  role_remap: { label: 'Role Remap', kind: 'role_pair' },
  default_radius_miles: { label: 'Discovery Radius', kind: 'decimal', suffix: 'miles' },
  nationwide_search_override: { label: 'Nationwide Search Override', kind: 'bool' },
  required_certifications: { label: 'Required Certifications', kind: 'json_list' },
  invoice_grouping_preference: { label: 'Invoice Grouping', kind: 'json_group' },
  default_invoice_terms: { label: 'Default Invoice Terms', kind: 'json_terms' },
};

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const RuleRow = ({ rule, meta, onToggle, onDelete }) => {
  const info = RULE_TYPE_META[rule.rule_type] || { label: rule.rule_type, kind: 'unknown' };
  const roleName = (id) => (meta.roles || []).find((r) => r.id === id)?.name || id;
  let valueText = '';
  if (info.kind === 'decimal') valueText = `${rule.decimal_value} ${info.suffix || ''}`;
  else if (info.kind === 'role_pair') valueText = `${roleName(rule.role_id)} -> ${roleName(rule.target_role_id)}`;
  else if (info.kind === 'bool') valueText = rule.bool_value ? 'ENABLED' : 'DISABLED';
  else if (info.kind === 'json_list' && Array.isArray(rule.json_value)) valueText = rule.json_value.join(', ');
  else if (info.kind === 'json_group' && rule.json_value?.grouping) valueText = rule.json_value.grouping;
  else if (info.kind === 'json_terms' && rule.json_value?.net_days != null) {
    valueText = `Net ${rule.json_value.net_days}${rule.json_value.auto_send ? ', auto-send' : ''}`;
  } else if (rule.text_value) valueText = rule.text_value;

  return (
    <div className={`flex items-center justify-between p-3 rounded border ${
      rule.is_active ? 'bg-zinc-900 border-zinc-700' : 'bg-zinc-950/50 border-zinc-800 opacity-60'
    }`} data-testid={`cbr-rule-${rule.rule_type}-${rule.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white">{info.label}</span>
          {!rule.is_active && <Badge className="bg-zinc-700 text-zinc-300 text-[10px]">disabled</Badge>}
        </div>
        <div className="text-xs text-zinc-400 mt-0.5 truncate">{valueText}</div>
        {rule.text_value && info.kind !== 'unknown' && (
          <div className="text-[11px] text-zinc-500 mt-0.5 italic truncate">{rule.text_value}</div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" variant="outline" onClick={onToggle}
                className="h-8 border-zinc-700 text-zinc-300" data-testid={`cbr-toggle-${rule.id}`}>
          {rule.is_active ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
        </Button>
        <Button size="sm" variant="outline" onClick={onDelete}
                className="h-8 border-red-800 text-red-400 hover:bg-red-900/30" data-testid={`cbr-delete-${rule.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

const AddRuleForm = ({ clientId, meta, onClose, onCreated }) => {
  const [ruleType, setRuleType] = useState('locked_rate');
  const [decimalValue, setDecimalValue] = useState('');
  const [textValue, setTextValue] = useState('');
  const [boolValue, setBoolValue] = useState(false);
  const [roleId, setRoleId] = useState('');
  const [targetRoleId, setTargetRoleId] = useState('');
  const [certList, setCertList] = useState('');
  const [grouping, setGrouping] = useState('per_event');
  const [netDays, setNetDays] = useState(30);
  const [autoSend, setAutoSend] = useState(true);
  const [saving, setSaving] = useState(false);
  const info = RULE_TYPE_META[ruleType] || {};

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const body = { client_id: clientId, rule_type: ruleType, text_value: textValue || null, is_active: true };
      if (info.kind === 'decimal') body.decimal_value = parseFloat(decimalValue);
      if (info.kind === 'role_pair') { body.role_id = roleId; body.target_role_id = targetRoleId || null; }
      if (info.kind === 'bool') body.bool_value = boolValue;
      if (info.kind === 'json_list') body.json_value = certList.split(',').map((s) => s.trim()).filter(Boolean);
      if (info.kind === 'json_group') body.json_value = { grouping };
      if (info.kind === 'json_terms') body.json_value = { net_days: parseInt(netDays, 10), auto_send: autoSend };
      await axios.post(`${API}/api/admin/client-business-rules`, body, { headers: authHeader() });
      toast.success('Rule saved');
      onCreated();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 bg-zinc-950 border border-cyan-800 rounded space-y-3" data-testid="cbr-add-rule-form">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">Add a new rule</h3>
        <Button size="sm" variant="ghost" onClick={onClose} className="text-zinc-400 h-7">Cancel</Button>
      </div>
      <div>
        <Label className="text-xs text-zinc-400">Rule type</Label>
        <Select value={ruleType} onValueChange={setRuleType}>
          <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white" data-testid="cbr-rule-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            {(meta.supported_rule_types || []).map((rt) => (
              <SelectItem key={rt} value={rt} data-testid={`cbr-rule-type-option-${rt}`}>
                {RULE_TYPE_META[rt]?.label || rt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {info.kind === 'decimal' && (
        <div>
          <Label className="text-xs text-zinc-400">Value ({info.suffix})</Label>
          <Input type="number" step="0.01" value={decimalValue} onChange={(e) => setDecimalValue(e.target.value)}
                 className="bg-zinc-900 border-zinc-700 text-white" data-testid="cbr-decimal-input" />
        </div>
      )}
      {info.kind === 'role_pair' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">Source role</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white" data-testid="cbr-source-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 max-h-64">
                {(meta.roles || []).map((r) => (<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Substitute role</Label>
            <Select value={targetRoleId} onValueChange={setTargetRoleId}>
              <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white" data-testid="cbr-target-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 max-h-64">
                {(meta.roles || []).map((r) => (<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      {info.kind === 'bool' && (
        <div className="flex items-center gap-3">
          <Switch checked={boolValue} onCheckedChange={setBoolValue} data-testid="cbr-bool-switch" />
          <Label className="text-sm text-white">{boolValue ? 'Enabled' : 'Disabled'}</Label>
        </div>
      )}
      {info.kind === 'json_list' && (
        <div>
          <Label className="text-xs text-zinc-400">Certification slugs (comma-separated)</Label>
          <Input placeholder="tabc, servsafe, ggf_certified" value={certList} onChange={(e) => setCertList(e.target.value)}
                 className="bg-zinc-900 border-zinc-700 text-white" data-testid="cbr-cert-input" />
        </div>
      )}
      {info.kind === 'json_group' && (
        <div>
          <Label className="text-xs text-zinc-400">Grouping</Label>
          <Select value={grouping} onValueChange={setGrouping}>
            <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white" data-testid="cbr-grouping-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              {(meta.invoice_grouping_options || []).map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      )}
      {info.kind === 'json_terms' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">Payment terms</Label>
            <Select value={String(netDays)} onValueChange={(v) => setNetDays(parseInt(v, 10))}>
              <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white" data-testid="cbr-net-days-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                {(meta.invoice_terms_options || []).map((o) => (<SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Switch checked={autoSend} onCheckedChange={setAutoSend} data-testid="cbr-auto-send-switch" />
            <Label className="text-sm text-white">Auto-send invoices</Label>
          </div>
        </div>
      )}
      <div>
        <Label className="text-xs text-zinc-400">Notes / reason (optional)</Label>
        <Input value={textValue} onChange={(e) => setTextValue(e.target.value)}
               placeholder="e.g. Negotiated rate per Master Services Agreement 2026-05"
               className="bg-zinc-900 border-zinc-700 text-white" data-testid="cbr-notes-input" />
      </div>
      <Button size="sm" onClick={handleSubmit} disabled={saving}
              className="bg-cyan-600 hover:bg-cyan-500 text-white w-full" data-testid="cbr-save-rule-btn">
        {saving ? 'Saving…' : 'Save rule'}
      </Button>
    </div>
  );
};

const ClientBusinessRulesConsole = () => {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [rules, setRules] = useState([]);
  const [meta, setMeta] = useState({ supported_rule_types: [], roles: [], invoice_grouping_options: [], invoice_terms_options: [] });
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/client-business-rules/clients`, {
        params: q ? { q } : {}, headers: authHeader(),
      });
      setClients(res.data.clients || []);
    } catch (e) { toast.error('Failed to load clients'); }
  }, [q]);

  const fetchMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/client-business-rules/meta`, { headers: authHeader() });
      setMeta(res.data || {});
    } catch (e) { /* silent */ }
  }, []);

  const fetchRules = useCallback(async (clientId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/client-business-rules/client/${clientId}`, {
        headers: authHeader(), params: { include_inactive: true },
      });
      setRules(res.data.rules || []);
    } catch (e) { toast.error('Failed to load rules'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);
  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { if (selectedClient) fetchRules(selectedClient.client_id); }, [selectedClient, fetchRules]);

  const handleToggle = async (rule) => {
    try {
      await axios.patch(`${API}/api/admin/client-business-rules/${rule.id}/toggle`, null, { headers: authHeader() });
      await fetchRules(selectedClient.client_id);
      toast.success(`Rule ${rule.is_active ? 'disabled' : 'enabled'}`);
    } catch (e) { toast.error('Toggle failed'); }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm('Deactivate this rule?')) return;
    try {
      await axios.delete(`${API}/api/admin/client-business-rules/${rule.id}`, { headers: authHeader() });
      await fetchRules(selectedClient.client_id);
      await fetchClients();
      toast.success('Rule removed');
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-client-business-rules-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">Client Business Rules</h1>
        <p className="text-zinc-400 text-sm mt-1">
          Configure locked rates, role remaps/blocks, discovery radius, invoice grouping, and default terms for enterprise clients.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2"><Users className="w-4 h-4" /> Clients</CardTitle>
            <div className="relative mt-3">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input placeholder="Search clients..." value={q} onChange={(e) => setQ(e.target.value)}
                     className="pl-9 bg-zinc-950 border-zinc-700 text-white" data-testid="cbr-client-search" />
            </div>
          </CardHeader>
          <CardContent className="space-y-1 max-h-[70vh] overflow-y-auto">
            {clients.length === 0 && <p className="text-sm text-zinc-500 italic px-1 py-4">No clients found.</p>}
            {clients.map((c) => (
              <button key={c.client_id} onClick={() => setSelectedClient(c)}
                className={`w-full text-left px-3 py-2 rounded flex items-center justify-between transition ${
                  selectedClient?.client_id === c.client_id ? 'bg-cyan-900/40 border border-cyan-700' : 'hover:bg-zinc-800/60 border border-transparent'
                }`} data-testid={`cbr-client-row-${c.client_id}`}>
                <div>
                  <div className="text-sm text-white font-medium truncate max-w-[180px]">{c.display_name}</div>
                  <div className="text-xs text-zinc-500 truncate max-w-[180px]">{c.email}</div>
                </div>
                <Badge className="bg-zinc-700 text-zinc-200 text-[10px]">
                  {c.rules_count} rule{c.rules_count === 1 ? '' : 's'}
                </Badge>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white text-base">
                  {selectedClient ? `Rules for ${selectedClient.display_name}` : 'Select a client'}
                </CardTitle>
                {selectedClient && <p className="text-xs text-zinc-500 mt-0.5">{selectedClient.email}</p>}
              </div>
              {selectedClient && (
                <Button size="sm" onClick={() => setShowAddForm(true)}
                        className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="cbr-add-rule-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add rule
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[70vh] overflow-y-auto">
            {!selectedClient && <p className="text-sm text-zinc-500 italic py-6 text-center">Choose a client from the list to view or edit their business rules.</p>}
            {selectedClient && rules.length === 0 && !loading && (
              <p className="text-sm text-zinc-500 italic py-6 text-center">
                No rules configured yet for {selectedClient.display_name}. Click <strong>Add rule</strong> to create one.
              </p>
            )}
            {loading && <p className="text-sm text-zinc-400 py-4 text-center">Loading rules…</p>}
            {rules.map((rule) => (
              <RuleRow key={rule.id} rule={rule} meta={meta}
                       onToggle={() => handleToggle(rule)} onDelete={() => handleDelete(rule)} />
            ))}
            {showAddForm && selectedClient && (
              <AddRuleForm clientId={selectedClient.client_id} meta={meta}
                onClose={() => setShowAddForm(false)}
                onCreated={async () => { setShowAddForm(false); await fetchRules(selectedClient.client_id); await fetchClients(); }} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientBusinessRulesConsole;
